// Knowledge-Bank effectiveness audit — API client.
// Backend: app/routes/kb_effectiveness_admin.py (/api/admin/kb-effectiveness).
// Every shape mirrors a Pydantic model there; the report itself mirrors
// services/agent_console/kb_effectiveness/report.py (KBEffectivenessReport).
// The page renders what a run STORED — nothing is recomputed client-side.

import api from './api';

export type RunState = 'scheduled' | 'running' | 'completed' | 'failed';
export type RunTrigger = 'schedule' | 'manual';
export type Severity = 'high' | 'medium' | 'low';

export type FindingKind =
  | 'write_error_rate'
  | 'misrouted_writes_live'
  | 'misrouted_writes_historic'
  | 'kb_before_sql'
  | 'playbook_list_before_sql'
  | 'playbook_follow'
  | 'matched_playbook_follow'
  | 'corrections_unrecorded'
  | 'near_dup_unconsolidated'
  | 'procedures_as_entries'
  | 'procedures_as_entries_llm'
  | 'missing_useful_for'
  | 'empty_own_playbook'
  | 'never_records'
  | 'bank_probe_failed'
  | '';

export interface Finding {
  severity: Severity;
  profile_id: string | null;
  title: string;
  evidence: string;
  kind: FindingKind;
}

export interface MountWrites {
  mount: string;
  label: string;
  target: string;
  calls: number;
  ok: number;
  error: number;
  unparsed: number;
  verbs: Record<string, number>;
  first_seen: string | null;
  last_seen: string | null;
}

export interface RecordModel {
  conversations: number;
  conversations_with_write: number;
  write_conversation_rate: number;
  writes: number;
  ok: number;
  error: number;
  unparsed: number;
  error_rate: number;
  unparsed_rate: number;
  new_entries: number;
  edits: number;
  playbook_upserts: number;
  deletes: number;
  entries_missing_useful_for: number;
  near_dup_warned: number;
  near_dup_followed_by_edit: number;
  shape_checked: number;
  playbook_shaped_entries: number;
  llm_shape_checked: number;
  llm_playbook_shaped: number;
  misrouted_writes: number;
  mounts: MountWrites[];
}

export interface ReadModel {
  turns: number;
  turns_with_tools: number;
  turns_with_sql: number;
  turns_kb_first: number;
  kb_first_rate: number;
  turns_kb_before_sql: number;
  kb_before_sql_rate: number;
  turns_playbook_list_before_sql: number;
  playbook_list_before_sql_rate: number;
  playbook_lists: number;
  playbook_lists_nonempty: number;
  playbook_gets_after_nonempty_list: number;
  playbook_follow_rate: number;
  matched_playbooks_offered: number;
  matched_playbooks_followed: number;
  kb_reads: number;
  kb_read_errors: number;
  banks_hit: Record<string, number>;
}

export interface CorrectionsModel {
  corrections: number;
  recorded: number;
  recorded_in_same_turn: number;
  recorded_rate: number;
}

export interface ProfileScorecard {
  profile_id: string;
  record: RecordModel;
  read: ReadModel;
  corrections: CorrectionsModel | null;
}

export interface BankStats {
  key: string;
  kind: 'playbook' | 'domain';
  name: string;
  label: string;
  mount: string;
  kb_id: string;
  playbook_count: number | null;
  entry_count: number | null;
  error: string | null;
}

export interface KBEffectivenessReport {
  generated_at: string;
  window_days: number;
  tool_calls: number;
  user_messages: number;
  profiles: ProfileScorecard[];
  banks: BankStats[];
  findings: Finding[];
}

// services/agent_console/kb_effectiveness/headline.py
export interface ProfileHeadline {
  profile_id: string;
  conversations: number;
  writes: number;
  write_error_rate: number | null;
  misrouted_writes: number;
  turns_with_sql: number;
  kb_before_sql_rate: number | null;
  playbook_list_before_sql_rate: number | null;
  near_dup_warned: number;
  near_dup_edit_rate: number | null;
  corrections: number;
  correction_recorded_rate: number | null;
}

export interface HeadlineRates {
  profiles: ProfileHeadline[];
  findings_by_severity: Partial<Record<Severity, number>>;
  tool_calls: number;
  user_messages: number;
}

export interface RunRead {
  id: string;
  state: RunState;
  trigger: RunTrigger;
  triggered_by_email: string | null;
  scheduled_at: string;
  started_at: string | null;
  finished_at: string | null;
  window_days: number;
  sample_size: number;
  llm_shape: boolean;
  probe_banks: boolean;
  headline: HeadlineRates | Record<string, never>;
  failed_step: 'lost' | 'audit' | 'persist' | null;
  error: string | null;
  detail: {
    warnings?: Record<string, string>;
    red_flags?: Array<{ profile_id: string; kind: string; flag_id: string | null; error?: string }>;
    findings?: number;
    profiles?: number;
    [key: string]: unknown;
  };
}

export interface RunDetail extends RunRead {
  report: KBEffectivenessReport | null;
}

export interface TrendPoint {
  run_id: string;
  finished_at: string | null;
  window_days: number;
  headline: HeadlineRates;
}

export interface RunScheduled {
  status: 'scheduled' | 'joined';
  detail: string;
  run: RunRead;
  created: boolean;
}

const BASE = '/admin/kb-effectiveness';

export const kbEffectivenessApi = {
  async listRuns(limit = 20): Promise<RunRead[]> {
    const r = await api.get<{ items: RunRead[] }>(`${BASE}/runs`, { params: { limit } });
    return r.data.items;
  },
  /** The newest completed run with its stored report; null when none has completed yet. */
  async latest(): Promise<RunDetail | null> {
    try {
      const r = await api.get<RunDetail>(`${BASE}/runs/latest`);
      return r.data;
    } catch (err) {
      if (isNotFound(err)) return null;
      throw err;
    }
  },
  async getRun(id: string): Promise<RunDetail> {
    const r = await api.get<RunDetail>(`${BASE}/runs/${id}`);
    return r.data;
  },
  async trend(limit = 26): Promise<TrendPoint[]> {
    const r = await api.get<{ points: TrendPoint[] }>(`${BASE}/trend`, { params: { limit } });
    return r.data.points;
  },
  async run(body?: { window_days?: number; llm_shape?: boolean }): Promise<RunScheduled> {
    const r = await api.post<RunScheduled>(`${BASE}/runs`, body ?? {});
    return r.data;
  },
};

function isNotFound(err: unknown): boolean {
  const status = (err as { response?: { status?: number } } | null)?.response?.status;
  return status === 404;
}
